import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCalendar,
  faClock,
  faUser,
  faBriefcase,
  faFlag,
  faEdit,
  faTrash,
  faInfoCircle,
  faChevronDown,
  faList,
  faExternalLinkAlt,
  faHourglass,
  faCircle,
  faSave,
  faBan,
  faPaperclip,
  faUpload,
  faLink,
  faFile,
  faSpinner,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { format, differenceInCalendarDays, isValid } from 'date-fns';
import Select from 'react-select';
import { TaskByEmployee, type TaskActivityFeedEntry, type TaskComment } from '@frontend/Models/Tasks/TaskModels';
import { parseLocalDate } from '@frontend/helpers/dateHelpers';
import MatterService from '@frontend/AppSettings/services/MatterService';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import TaskService from '@frontend/AppSettings/services/TaskService';
import toastService from '@frontend/AppSettings/services/ToastService';
import { isLegalEnvironment } from '@frontend/AppSettings/environmentConfig';
import StatusBadge from '@frontend/Components/StatusBadge/StatusBadge';
import ConfirmationModal from '@frontend/Components/ConfirmationModal/ConfirmationModal';
import { Matter } from '@frontend/Models/Matter/MatterModels';
import { Employees } from '@frontend/Models/Employee/EmployeeModels';
import { SelectOption } from '@frontend/Models/Form/FormModels';
import useActiveEmployeeId from '@frontend/AppSettings/hooks/useActiveEmployeeId';
import { UpdateTaskRequest } from '@frontend/Models/Tasks/TaskRequestResponseModels';
import { useTaskDocuments, type SharePointFile } from './useTaskDocuments';
import SharePointFileBrowser from './SharePointFileBrowser';
import './ActionItemDetailsPanel.css';

interface ActionItemDetailsPanelProps {
  task: TaskByEmployee | null;
  onClose: () => void;
  isOpen: boolean;
  onEdit?: (updatedTask?: TaskByEmployee) => void;
  onDelete?: () => void;
}

const COMMENT_MAX_LENGTH = 1000;

const ActionItemDetailsPanel: React.FC<ActionItemDetailsPanelProps> = ({
  task,
  onClose,
  isOpen,
  onEdit,
  onDelete,
}) => {
  const employeeId = useActiveEmployeeId();
  const queryClient = useQueryClient();
  const [matterDetails, setMatterDetails] = useState<any>(null);
  const [assignedLeader, setAssignedLeader] = useState<any>(null);
  const [currentStatus, setCurrentStatus] = useState<string>(task?.status || 'Pending');
  const [showActivityFeed, setShowActivityFeed] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [expandedCommentIndex, setExpandedCommentIndex] = useState<number | null>(null);

  // Documents state
  const [showDocuments, setShowDocuments] = useState(false);
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLinkingFiles, setIsLinkingFiles] = useState(false);
  const [removeConfirmId, setRemoveConfirmId] = useState<string | null>(null);
  const { documents, isLoading: isLoadingDocs, uploadFile, linkFile, removeFile } = useTaskDocuments(task?.taskid);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    task_title: '',
    matterid: '',
    assignedto: '',
    duedate: '',
    description: '',
    task_type: '',
    status: 'Pending',
    priority: '',
  });
  const [matters, setMatters] = useState<SelectOption[]>([]);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [loadingMatters, setLoadingMatters] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState<Employees | null>(null);
  const [allEmployeesForNames, setAllEmployeesForNames] = useState<Employees[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [error, setError] = useState<string>('');
  
  const matterOrProjectLabel = isLegalEnvironment ? 'Matter' : 'Project';

  // Parse date-only string (YYYY-MM-DD) as local date to avoid UTC-midnight shifting the day
  const parseDateOnlyAsLocal = (s: string | null | undefined): Date | null => {
    if (!s) return null;
    const d = parseLocalDate(s);
    return isValid(d) ? d : null;
  };

  // Get display priority: use task.priority when available, else derive from due date
  const getDisplayPriority = useCallback((t: TaskByEmployee): string => {
    if (t.priority) return t.priority;
    const due = parseDateOnlyAsLocal(t.duedate);
    if (!due) return 'None';
    const days = differenceInCalendarDays(due, new Date());
    if (days < 0 || days === 0) return 'Urgent';
    if (days <= 1) return 'High';
    if (days <= 7) return 'Medium';
    return 'Low';
  }, []);

  useEffect(() => {
    if (!task) {
      // Clear data when task is null
      setMatterDetails(null);
      setAssignedLeader(null);
      return;
    }
    
    setCurrentStatus(task.status || 'Pending');

    let isCancelled = false;

    const load = async () => {
      try {
        if (task.matterid) {
          const matter = await MatterService.getMatterDetails(task.matterid);
          if (!isCancelled) setMatterDetails(matter);
        } else {
          // Clear matter details if matterid is null
          if (!isCancelled) setMatterDetails(null);
        }
      } catch (error) {
        console.error('Error fetching matter details:', error);
        if (!isCancelled) setMatterDetails(null);
      }

      try {
        if (task.assignedto) {
          const employees = await EmployeeService.getEmployees();
          const leader = employees.find(
            (emp: any) => emp.employeeid === task.assignedto || emp.EmployeeID === task.assignedto
          );
          if (!isCancelled) setAssignedLeader(leader || null);
          if (!isCancelled) setAllEmployeesForNames(employees || []);
        } else {
          if (!isCancelled) setAssignedLeader(null);
          const employees = await EmployeeService.getEmployees();
          if (!isCancelled) setAllEmployeesForNames(employees || []);
        }
      } catch (error) {
        console.error('Error fetching assigned employee:', error);
        if (!isCancelled) setAssignedLeader(null);
        if (!isCancelled) setAllEmployeesForNames([]);
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [task]);

  // Initialize form data when task changes or edit mode is enabled
  useEffect(() => {
    if (task && isEditMode) {
      // Convert due date to YYYY-MM-DD for date input. Use date-only string as-is to avoid
      // timezone shift (new Date("2025-10-22") is UTC midnight → previous day in US tz).
      const formatDateForInput = (dateString: string | null | undefined): string => {
        if (!dateString) return '';
        const trimmed = String(dateString).trim();
        const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateOnlyMatch) {
          const [, y, m, d] = dateOnlyMatch;
          return `${y}-${m}-${d}`;
        }
        const date = parseLocalDate(dateString);
        if (!isValid(date)) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      setFormData({
        task_title: task.task_title || '',
        matterid: task.matterid || '',
        assignedto: task.assignedto || '',
        duedate: formatDateForInput(task.duedate),
        description: task.description || '',
        task_type: task.task_type || '',
        status: task.status || 'Pending',
        priority: task.priority || (getDisplayPriority(task) === 'None' ? '' : getDisplayPriority(task)),
      });
    }
  }, [task, isEditMode, getDisplayPriority]);

  // Collapse expanded comment when switching to another task
  useEffect(() => {
    setExpandedCommentIndex(null);
  }, [task]);

  // Fetch matters for dropdown
  useEffect(() => {
    const fetchMatters = async () => {
      if (!isEditMode || !currentEmployee || !employeeId) return;

      try {
        setLoadingMatters(true);
        let data: Matter[];
        const permishIdNum = parseInt(currentEmployee.permish_id?.toString() || '0');

        if (permishIdNum === 1) {
          data = await MatterService.getMatters();
        } else if (permishIdNum === 2 || permishIdNum === 3) {
          data = await MatterService.getProjectMattersByEmployee(employeeId);
        } else {
          data = [];
        }

        if (data) {
          const options: SelectOption[] = data.map((matter) => ({
            value: matter.matterid,
            label: `${matter.matterid}: ${matter.mattername || ''}`,
          }));
          setMatters(options);
        }
      } catch (error) {
        console.error('Error fetching matters:', error);
      } finally {
        setLoadingMatters(false);
      }
    };

    if (isEditMode && currentEmployee) {
      fetchMatters();
    }
  }, [isEditMode, currentEmployee, employeeId]);

  // Fetch employees for dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!isEditMode) return;

      try {
        setLoadingEmployees(true);
        const data: Employees[] = await EmployeeService.getEmployees();
        const options: SelectOption[] = data.map((employee) => ({
          value: employee.EmployeeID,
          label: `${employee.FirstName} ${employee.LastName}`,
        }));
        setEmployees(options);

        const currentEmp = data.find((emp) => emp.EmployeeID === employeeId);
        if (currentEmp) {
          setCurrentEmployee(currentEmp);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      } finally {
        setLoadingEmployees(false);
      }
    };

    if (isEditMode) {
      fetchEmployees();
    }
  }, [isEditMode, employeeId]);

  if (!task || !isOpen) return null;

  const getDaysRemaining = () => {
    const dueDate = parseDateOnlyAsLocal(task.duedate);
    if (!dueDate) return null;
    try {
      const today = new Date();
      return differenceInCalendarDays(dueDate, today);
    } catch {
      return null;
    }
  };

  const daysRemaining = getDaysRemaining();
  const dueStatusClass =
    daysRemaining === null
      ? ''
      : daysRemaining < 0
      ? 'overdue'
      : daysRemaining === 0
      ? 'due-today'
      : 'on-track';
  // Note: createddate and updateddate may not be available in TaskByEmployee type
  // Using type assertion to access potentially missing fields
  const taskWithDates = task as TaskByEmployee & { createddate?: string; updateddate?: string };
  
  // Format dates with validation (parse date-only as local so display is correct)
  const formatDateSafely = (dateString: string | null | undefined): string | null => {
    const date = parseDateOnlyAsLocal(dateString);
    if (!date) return null;
    return format(date, 'MMM d, yyyy');
  };
  
  const createdDate = formatDateSafely(taskWithDates.createddate);
  const dueDate = formatDateSafely(task.duedate);
  const lastUpdated = formatDateSafely(taskWithDates.updateddate) || '2h ago';

  const leaderName = (() => {
    if (assignedLeader) {
      const first = assignedLeader.firstname || assignedLeader.FirstName || '';
      const last = assignedLeader.lastname || assignedLeader.LastName || '';
      const full = `${first} ${last}`.trim();
      if (full) return full;
    }
    return task.assignedto || 'Unassigned';
  })();

  const leaderInitials =
    leaderName && leaderName !== 'Unassigned'
      ? leaderName
          .split(' ')
          .filter(Boolean)
          .map((part) => part[0]?.toUpperCase() || '')
          .join('')
          .slice(0, 2) || '?'
      : '?';

  // Get selected employee details for edit mode
  const selectedEmployeeInEdit = isEditMode && formData.assignedto
    ? employees.find((emp) => emp.value === formData.assignedto)
    : null;
  
  // Resolve activity feed employee_id to display name
  const getEmployeeDisplayName = (employeeId: string): string => {
    if (!employeeId) return 'Unknown';
    const emp = allEmployeesForNames.find(
      (e: any) => (e.EmployeeID && String(e.EmployeeID) === String(employeeId)) || (e.employeeid && String(e.employeeid) === String(employeeId))
    );
    if (emp) {
      const first = emp.FirstName ?? '';
      const last = emp.LastName ?? '';
      return `${first} ${last}`.trim() || employeeId;
    }
    return employeeId;
  };

  // Human-readable, descriptive titles for activity feed entries
  const getActivityEntryLabel = (entry: TaskActivityFeedEntry): string => {
    const fieldLabels: Record<string, string> = {
      task_title: 'title',
      description: 'description',
      duedate: 'due date',
      assignedto: 'assigned to',
      status: 'status',
      task_type: 'scope',
      priority: 'priority',
      matterid: 'matter',
    };

    switch (entry.action) {
      case 'created': {
        const d = entry.details as { task_title?: string; status?: string } | undefined;
        const title = d?.task_title ? `"${d.task_title}"` : 'this action item';
        const status = d?.status ? ` and set its initial status to ${d.status}` : '';
        return `Created the action item ${title}${status}.`;
      }
      case 'status_changed': {
        const d = entry.details as { old_value?: string; new_value?: string } | undefined;
        const oldV = d?.old_value ?? '—';
        const newV = d?.new_value ?? '—';
        return `Updated the action item's status from ${oldV} to ${newV}.`;
      }
      case 'task_updated': {
        const d = entry.details as Record<string, { old_value?: unknown; new_value?: unknown }> | undefined;
        if (!d || Object.keys(d).length === 0) return 'Updated one or more details of this action item.';
        const parts = Object.entries(d).map(([field, v]) => {
          const label = fieldLabels[field] || field.replace(/_/g, ' ');
          const oldStr = v?.old_value != null && v?.old_value !== '' ? String(v.old_value) : '(none)';
          const newStr = v?.new_value != null && v?.new_value !== '' ? String(v.new_value) : '(none)';
          return `Changed the ${label} from ${oldStr} to ${newStr}`;
        });
        return parts.join('. ');
      }
      default:
        return entry.action ? `Recorded activity: ${entry.action}.` : 'Activity recorded.';
    }
  };

  // Parse and sort task comments (handles null, non-array, or legacy shape)
  const getCommentsList = (): TaskComment[] => {
    const raw = task?.comments;
    if (raw == null) return [];
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((c): c is TaskComment => c != null && typeof c === 'object' && 'timestamp' in c && 'employee_id' in c && 'text' in c)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  };

  const commentsList = getCommentsList();

  const statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Completed', label: 'Completed' },
  ];

  // Normalize status for Select value (handles API casing like "in progress" vs "In Progress")
  const getStatusSelectValue = () => {
    const status = isEditMode ? formData.status : currentStatus;
    const normalized = statusOptions.find(
      (opt) => opt.value.toLowerCase() === (status || '').toLowerCase()
    );
    return normalized || statusOptions.find((opt) => opt.value === status) || null;
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!task?.taskid || isDeleting) return;

    setIsDeleting(true);
    
    // Show info toast that deletion is in progress
    const deletingToastId = toastService.info('Deleting action item...', {
      description: `Removing "${task.task_title}"`,
      duration: 0, // Persistent until we dismiss it
    });
    
    try {
      await TaskService.deleteTask(task.taskid);
      
      // Dismiss the deleting toast
      toastService.dismiss(deletingToastId);
      
      // Success - result contains message and sept_tenantid
      toastService.success('Action item deleted', {
        description: `"${task.task_title}" has been permanently removed.`,
        duration: 5000,
      });
      
      // Call the onDelete callback if provided (for parent to handle refresh, etc.)
      if (onDelete) {
        onDelete();
      }
      // Close the panel after successful deletion
      onClose();
    } catch (error: any) {
      console.error('Error deleting task:', error);
      
      // Dismiss the deleting toast if it's still showing
      toastService.dismiss(deletingToastId);
      
      // Show detailed error message
      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error occurred';
      toastService.error('Failed to delete action item', {
        description: errorMessage || 'Please try again or contact support if the issue persists.',
        duration: 7000,
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  // Task type, priority, and status options
  const taskTypeOptions = [
    { value: 'Internal', label: 'Internal' },
    { value: 'External', label: 'External' },
  ];

  const priorityOptions = [
    { value: '', label: 'None' },
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Urgent', label: 'Urgent' },
  ];

  // Edit mode handlers
  const handleEditClick = () => {
    setIsEditMode(true);
    if (onEdit) {
      onEdit();
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleMatterChange = (selectedOption: SelectOption | null) => {
    setFormData({
      ...formData,
      matterid: selectedOption ? selectedOption.value : '',
    });
  };

  const handleTaskTypeChange = (selectedOption: SelectOption | null) => {
    setFormData({
      ...formData,
      task_type: selectedOption ? selectedOption.value : '',
    });
  };

  const handlePriorityChange = (selectedOption: SelectOption | null) => {
    setFormData({
      ...formData,
      priority: selectedOption ? selectedOption.value : '',
    });
  };

  const handleAssignedToChange = (selectedOption: SelectOption | null) => {
    setFormData({
      ...formData,
      assignedto: selectedOption ? selectedOption.value : '',
    });
  };

  const handleStatusChange = (selectedOption: SelectOption | null) => {
    const newStatus = selectedOption ? selectedOption.value : 'Pending';
    setFormData({ ...formData, status: newStatus });
    setCurrentStatus(newStatus);
  };

  // Persist status change when updated from dropdown in view mode
  const handleStatusChangeViewMode = async (selectedOption: SelectOption | null) => {
    if (!task?.taskid || !selectedOption || isUpdatingStatus) return;
    const newStatus = selectedOption.value;
    if (newStatus === currentStatus) return;

    const previousStatus = currentStatus;
    setCurrentStatus(newStatus); // Optimistic update
    setIsUpdatingStatus(true);

    try {
      const response = await TaskService.updateTask(task.taskid, { status: newStatus, acting_employee_id: employeeId ?? undefined });
      await queryClient.invalidateQueries({ queryKey: ['tasks', 'all'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });

      if (response.task && onEdit) {
        const formattedUpdatedTask: TaskByEmployee = {
          taskid: response.task.taskid,
          matterid: response.task.matterid,
          mattername: response.task.mattername,
          task_title: response.task.task_title,
          description: response.task.description,
          duedate: response.task.duedate,
          assignedto: response.task.assignedto,
          status: response.task.status,
          createdby: response.task.createdby,
          task_type: response.task.task_type,
          sept_tenantid: response.task.sept_tenantid,
          priority: response.task.priority ?? undefined,
          activity_feed: response.task.activity_feed ?? undefined,
          comments: response.task.comments ?? undefined,
        };
        onEdit(formattedUpdatedTask);
      }
      toastService.success('Status updated', {
        description: `Changed to "${newStatus}"`,
        duration: 3000,
      });
    } catch (error: any) {
      setCurrentStatus(previousStatus); // Revert on failure
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update status';
      toastService.error('Failed to update status', {
        description: errorMessage,
        duration: 5000,
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleSave = async () => {
    if (!task?.taskid) return;

    setIsSaving(true);
    setError('');

    if (!formData.task_title || !formData.matterid || !formData.assignedto) {
      const missingFields = [];
      if (!formData.task_title) missingFields.push('Task Title');
      if (!formData.matterid) missingFields.push('Matter/Project');
      if (!formData.assignedto) missingFields.push('Assigned to');
      
      toastService.warning('Required fields missing', {
        description: `Please fill in: ${missingFields.join(', ')}`,
        duration: 5000,
      });
      setIsSaving(false);
      return;
    }

    // Show info toast that save is in progress
    const savingToastId = toastService.info('Saving changes...', {
      description: 'Updating task details',
      duration: 0, // Persistent until we dismiss it
    });

    try {
      // Resolve assignedto to employee ID (backend expects ID; task.assignedto may be display name)
      const assignedToOption = employees.find(
        (o) => o.value === formData.assignedto || o.label === formData.assignedto
      );
      const assignedToId = assignedToOption ? assignedToOption.value : formData.assignedto;

      // Build the update payload with all form fields
      const updatePayload: UpdateTaskRequest = {
        task_title: formData.task_title,
        matterid: formData.matterid,
        assignedto: assignedToId,
        status: formData.status,
        task_type: formData.task_type,
        description: formData.description || '',
        duedate: formData.duedate || null,
        priority: formData.priority || null,
        acting_employee_id: employeeId ?? undefined,
      };

      // Call the updateTask endpoint
      const response = await TaskService.updateTask(task.taskid, updatePayload);

      // Dismiss the saving toast
      toastService.dismiss(savingToastId);

      if (response.message.includes('error')) {
        throw new Error(response.message);
      }

      // Invalidate and refetch tasks query to show the updated task
      await queryClient.invalidateQueries({ queryKey: ['tasks', 'all'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      
      // Get the updated task from the response
      const updatedTask = response.task;
      
      // Update current status if it changed
      if (formData.status !== task.status) {
        setCurrentStatus(formData.status);
      }
      
      // Close edit mode
      setIsEditMode(false);
      
      // Call onEdit callback with the updated task data (parent can update selectedTask)
      if (onEdit && updatedTask) {
        // Convert the updated task to match TaskByEmployee format
        const formattedUpdatedTask: TaskByEmployee = {
          taskid: updatedTask.taskid,
          matterid: updatedTask.matterid,
          mattername: updatedTask.mattername,
          task_title: updatedTask.task_title,
          description: updatedTask.description,
          duedate: updatedTask.duedate,
          assignedto: updatedTask.assignedto,
          status: updatedTask.status,
          createdby: updatedTask.createdby,
          task_type: updatedTask.task_type,
          sept_tenantid: updatedTask.sept_tenantid,
          priority: updatedTask.priority ?? undefined,
          activity_feed: updatedTask.activity_feed ?? undefined,
          comments: updatedTask.comments ?? undefined,
        };
        onEdit(formattedUpdatedTask);
      } else if (onEdit) {
        // If no updated task in response, just call the callback
        onEdit();
      }
      
      // Build a detailed success message showing what changed
      const changedFields: string[] = [];
      if (formData.task_title !== task.task_title) changedFields.push('title');
      if (formData.status !== task.status) changedFields.push('status');
      if (formData.assignedto !== task.assignedto) changedFields.push('assigned to');
      if (formData.matterid !== task.matterid) changedFields.push('matter/project');
      if (formData.task_type !== task.task_type) changedFields.push('scope');
      const prevPriority = task.priority || (getDisplayPriority(task) === 'None' ? '' : getDisplayPriority(task));
      if (formData.priority !== prevPriority) changedFields.push('priority');
      const taskDueDateOnly = (() => {
        if (!task.duedate) return '';
        const trimmed = String(task.duedate).trim();
        const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateOnlyMatch) {
          const [, y, m, d] = dateOnlyMatch;
          return `${y}-${m}-${d}`;
        }
        const parsed = parseLocalDate(task.duedate);
        if (!isValid(parsed)) return '';
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      })();
      if (formData.duedate !== taskDueDateOnly) changedFields.push('due date');
      if (formData.description !== (task.description || '')) changedFields.push('description');
      
      const changesDescription = changedFields.length > 0
        ? `Updated: ${changedFields.join(', ')}`
        : 'All changes have been saved';
      
      toastService.success('Action item updated', {
        description: `"${formData.task_title}" - ${changesDescription}`,
        duration: 5000,
      });
    } catch (error: any) {
      console.error('Error saving task:', error);
      
      // Dismiss the saving toast if it's still showing
      toastService.dismiss(savingToastId);
      
      // Show detailed error message
      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error occurred';
      toastService.error('Failed to update action item', {
        description: errorMessage || 'Please try again or contact support if the issue persists.',
        duration: 7000,
      });
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddComment = async () => {
    const text = newCommentText.trim();
    if (!text || !task?.taskid || !employeeId || isAddingComment) return;

    setIsAddingComment(true);
    setNewCommentText('');

    const newComment: TaskComment = {
      timestamp: new Date().toISOString(),
      employee_id: String(employeeId),
      text,
    };
    const currentComments = getCommentsList();
    const updatedComments: TaskComment[] = [...currentComments, newComment];

    try {
      const response = await TaskService.updateTask(task.taskid, {
        comments: updatedComments,
        acting_employee_id: employeeId ?? undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['tasks', 'all'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (response.task && onEdit) {
        const formattedUpdatedTask: TaskByEmployee = {
          taskid: response.task.taskid,
          matterid: response.task.matterid,
          mattername: response.task.mattername,
          task_title: response.task.task_title,
          description: response.task.description,
          duedate: response.task.duedate,
          assignedto: response.task.assignedto,
          status: response.task.status,
          createdby: response.task.createdby,
          task_type: response.task.task_type,
          sept_tenantid: response.task.sept_tenantid,
          priority: response.task.priority ?? undefined,
          activity_feed: response.task.activity_feed ?? undefined,
          comments: response.task.comments ?? undefined,
        };
        onEdit(formattedUpdatedTask);
      }
      toastService.success('Comment added', { duration: 3000 });
    } catch (error: any) {
      setNewCommentText(text);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to add comment';
      toastService.error('Failed to add comment', { description: errorMessage, duration: 5000 });
    } finally {
      setIsAddingComment(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      await uploadFile(file);
      toastService.success('File uploaded', {
        description: `"${file.name}" has been attached to this action item.`,
        duration: 4000,
      });
    } catch (err: any) {
      const msg = err?.message || 'Failed to upload file';
      toastService.error('Upload failed', { description: msg, duration: 5000 });
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleFilesLinked = async (files: SharePointFile[]) => {
    setIsLinkingFiles(true);
    try {
      for (const file of files) {
        await linkFile(file.id, file.name);
      }
      setShowFileBrowser(false);
      toastService.success('Files linked', {
        description: `${files.length} file${files.length !== 1 ? 's' : ''} linked to this action item.`,
        duration: 4000,
      });
    } catch (err: any) {
      const msg = err?.message || 'Failed to link files';
      toastService.error('Link failed', { description: msg, duration: 5000 });
    } finally {
      setIsLinkingFiles(false);
    }
  };

  const handleRemoveDocument = async () => {
    if (!removeConfirmId) return;
    try {
      await removeFile(removeConfirmId);
      toastService.success('Document removed', { duration: 3000 });
    } catch (err: any) {
      const msg = err?.message || 'Failed to remove document';
      toastService.error('Remove failed', { description: msg, duration: 5000 });
    } finally {
      setRemoveConfirmId(null);
    }
  };

  return (
    <div className={`action-item-details-overlay ${isOpen ? 'open' : ''}`} onClick={onClose}>
      <div className="action-item-details-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="action-item-details-header">
          <h2 className="action-item-details-title">Action Item Details</h2>
          <button
            type="button"
            className="action-item-details-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="action-item-details-content">
          {/* Main Title */}
          {isEditMode ? (
            <input
              type="text"
              name="task_title"
              value={formData.task_title}
              onChange={handleInputChange}
              placeholder="Enter task title"
              maxLength={255}
              className="action-item-edit-title-input"
              required
            />
          ) : (
            <h1 className="action-item-main-title">{task.task_title || 'Untitled Action Item'}</h1>
          )}

          {/* Metadata */}
          <div className="action-item-metadata">
            {createdDate && (
              <div className="action-item-meta-item">
                <FontAwesomeIcon icon={faCalendar} className="meta-icon" />
                <span>Created {createdDate}</span>
              </div>
            )}
            {lastUpdated && (
              <div className="action-item-meta-item">
                <FontAwesomeIcon icon={faClock} className="meta-icon" />
                <span>Last updated 2h ago</span>
              </div>
            )}
          </div>

          {/* Description Widget */}
          {(isEditMode || task.description) && (
            <div className="action-item-section">
              <div className="action-item-widget action-item-description-widget">
                <div className="action-item-section-header">
                  <FontAwesomeIcon icon={faList} className="action-item-section-icon" />
                  <h3 className="action-item-section-title">Description</h3>
                </div>
                {isEditMode ? (
                  <textarea
                    name="description"
                    value={formData.description || ''}
                    onChange={handleInputChange}
                    placeholder="Provide additional context, requirements, or notes..."
                    rows={6}
                    className="action-item-edit-description-textarea"
                    maxLength={500}
                  />
                ) : (
                  <p className="action-item-description">{task.description || ''}</p>
                )}
              </div>
            </div>
          )}

          {/* Current Status Widget */}
          <div className="action-item-section">
            <div className="action-item-widget action-item-status-widget">
              <div className="action-item-section-header">
                <div className="action-item-status-icon-wrapper">
                  <FontAwesomeIcon icon={faCircle} className="status-dot-icon" />
                </div>
                <h3 className="action-item-section-title">Current Status</h3>
              </div>
              <Select
                options={statusOptions}
                value={getStatusSelectValue()}
                onChange={isEditMode ? handleStatusChange : handleStatusChangeViewMode}
                isDisabled={!isEditMode && isUpdatingStatus}
                className="action-item-status-select"
                classNamePrefix="action-item-select"
              />
              <p className="action-item-status-note">
                <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
                Status changes are tracked in activity feed
              </p>
            </div>
          </div>

          <div className="action-item-row">
            {/* Assigned to Widget */}
            <div className="action-item-section action-item-card">
              <div className="action-item-widget action-item-leader-widget">
                {isEditMode ? (
                  <>
                    <div className="action-item-section-header">
                      <FontAwesomeIcon icon={faUser} className="action-item-section-icon" />
                      <h3 className="action-item-section-title">Assigned to</h3>
                    </div>
                  <Select
                    options={employees}
                    isLoading={loadingEmployees}
                    value={selectedEmployeeInEdit || null}
                    onChange={handleAssignedToChange}
                    placeholder="Select or search an employee..."
                    className="action-item-leader-select-edit"
                    classNamePrefix="action-item-select"
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    formatOptionLabel={(option) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className="action-item-leader-avatar-small">
                          {option.label
                            .split(' ')
                            .filter(Boolean)
                            .map((part: string) => part[0]?.toUpperCase() || '')
                            .join('')
                            .slice(0, 2)}
                        </div>
                        <span>{option.label}</span>
                      </div>
                    )}
                  />
                  </>
                ) : (
                  <div className="action-item-assignment-display">
                    <div className="action-item-assigner-row action-item-assignment-first-row">
                      <div className="action-item-assigner-avatar">
                        {leaderInitials}
                      </div>
                      <div className="action-item-assigner-info">
                        <span className="action-item-assigned-by-label">ASSIGNED TO</span>
                        <span className="action-item-assigned-by-name">
                          {leaderName}
                        </span>
                      </div>
                    </div>
                    {task.createdby && (
                      <div className="action-item-assigner-row">
                        <div className="action-item-assigner-avatar">
                          {getEmployeeDisplayName(task.createdby)
                            .split(' ')
                            .filter(Boolean)
                            .map((part) => part[0]?.toUpperCase() || '')
                            .join('')
                            .slice(0, 2) || '?'}
                        </div>
                        <div className="action-item-assigner-info">
                          <span className="action-item-assigned-by-label">Assigned by</span>
                          <span className="action-item-assigned-by-name">
                            {getEmployeeDisplayName(task.createdby)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Due Date Widget */}
            <div className="action-item-section action-item-card">
              <div className="action-item-widget action-item-due-date-widget">
                <div className="action-item-section-header">
                  <FontAwesomeIcon icon={faCalendar} className="action-item-section-icon" />
                  <h3 className="action-item-section-title">Due Date</h3>
                </div>
                {isEditMode ? (
                  <input
                    type="date"
                    name="duedate"
                    value={formData.duedate}
                    onChange={handleInputChange}
                    className="action-item-edit-date-input"
                  />
                ) : dueDate ? (
                  <div className="action-item-due-date-content">
                    <div className="action-item-due-date-row">
                      <FontAwesomeIcon icon={faFlag} className="due-date-icon" />
                      <div className="action-item-due-date">{dueDate}</div>
                    </div>
                    {daysRemaining !== null && (
                      <div className="action-item-due-date-row">
                        <FontAwesomeIcon icon={faHourglass} className="due-date-icon" />
                        <div className={`action-item-days-remaining ${dueStatusClass}`}>
                          {daysRemaining > 0
                            ? `${daysRemaining} days remaining`
                            : daysRemaining === 0
                            ? 'Due today'
                            : `${Math.abs(daysRemaining)} days overdue`}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="action-item-due-date">No due date</div>
                )}
              </div>
            </div>
          </div>

          {/* Matter/Project Context Widget */}
          {(matterDetails || task.mattername) && (
            <div className="action-item-section">
              <div className="action-item-widget action-item-matter-widget">
                <div className="action-item-section-header">
                  <FontAwesomeIcon icon={faBriefcase} className="action-item-section-icon" />
                  <h3 className="action-item-section-title">{matterOrProjectLabel} Context</h3>
                  <button type="button" className="action-item-link action-item-matter-open">
                    Open <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </button>
                </div>

                <div className="action-item-matter-content">
                  <div className="action-item-matter-inner-card">
                    <div className="action-item-matter-inner-header">
                      <div>
                        <div className="action-item-matter-label">{matterOrProjectLabel.toUpperCase()}</div>
                        {isEditMode ? (
                          <Select
                            options={matters}
                            isLoading={loadingMatters}
                            value={matters.find((matter) => matter.value === formData.matterid) || null}
                            onChange={handleMatterChange}
                            placeholder="Search and select matter/project"
                            className="action-item-status-select"
                            classNamePrefix="action-item-select"
                            isClearable
                          />
                        ) : (
                          <div className="action-item-matter-name">
                            {matterDetails?.mattername || task.mattername || 'N/A'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="action-item-matter-grid">
                    <div className="action-item-matter-grid-item">
                      <div className="action-item-matter-grid-label">CLIENT</div>
                      <div className="action-item-matter-grid-value">
                        {matterDetails?.clientid && matterDetails?.client_name ? (
                          <>
                            <strong>{matterDetails.clientid}</strong> - {matterDetails.client_name}
                          </>
                        ) : matterDetails?.clientid && matterDetails?.clientname ? (
                          <>
                            <strong>{matterDetails.clientid}</strong> - {matterDetails.clientname}
                          </>
                        ) : matterDetails?.client_name || matterDetails?.clientname ? (
                          matterDetails.client_name || matterDetails.clientname
                        ) : (
                          'N/A'
                        )}
                      </div>
                    </div>

                    <div className="action-item-matter-grid-item">
                      <div className="action-item-matter-grid-label">MATTER STATUS</div>
                      <div className="action-item-matter-grid-value">
                        <StatusBadge status={matterDetails?.status || 'Active'} />
                      </div>
                    </div>

                    <div className="action-item-matter-grid-item action-item-matter-grid-item-muted">
                      <div className="action-item-matter-grid-label">SCOPE</div>
                      {isEditMode ? (
                        <Select
                          options={taskTypeOptions}
                          value={taskTypeOptions.find((t) => t.value === formData.task_type) || null}
                          onChange={handleTaskTypeChange}
                          placeholder="Select scope"
                          className="action-item-status-select"
                          classNamePrefix="action-item-select"
                          isClearable
                        />
                      ) : (
                        <div className="action-item-matter-grid-value">{task.task_type || 'N/A'}</div>
                      )}
                    </div>

                    <div className="action-item-matter-grid-item action-item-matter-grid-item-muted">
                      <div className="action-item-matter-grid-label">PRIORITY</div>
                      {isEditMode ? (
                        <Select
                          options={priorityOptions}
                          value={priorityOptions.find((p) => p.value === formData.priority) || null}
                          onChange={handlePriorityChange}
                          placeholder="Select priority"
                          className="action-item-status-select"
                          classNamePrefix="action-item-select"
                          isClearable
                        />
                      ) : (
                        <div className="action-item-matter-grid-value">
                          <StatusBadge
                            status={getDisplayPriority(task)}
                            variant={
                              ['Urgent', 'High'].includes(getDisplayPriority(task))
                                ? 'at-risk'
                                : getDisplayPriority(task) === 'Medium'
                                ? 'pending'
                                : 'active'
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Activity Feed Section */}
          <div className="action-item-section">
            <button
              className="action-item-collapsible-header"
              onClick={() => setShowActivityFeed(!showActivityFeed)}
            >
              <h3 className="action-item-section-title">Activity Feed</h3>
              <div className="action-item-count-badge">
                {Array.isArray((task as TaskByEmployee).activity_feed) ? (task as TaskByEmployee).activity_feed!.length : 0}
              </div>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`action-item-chevron ${showActivityFeed ? 'open' : ''}`}
              />
            </button>
            {showActivityFeed && (
              <div className="action-item-collapsible-content">
                {Array.isArray((task as TaskByEmployee).activity_feed) && (task as TaskByEmployee).activity_feed!.length > 0 ? (
                  <ul className="action-item-activity-list">
                    {[...(task as TaskByEmployee).activity_feed!]
                      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                      .map((entry, idx) => {
                        const e = entry as TaskActivityFeedEntry;
                        return (
                          <li key={idx} className="action-item-activity-item">
                            <div className="action-item-activity-dot" />
                            <div className="action-item-activity-body">
                              <p className="action-item-activity-label">{getActivityEntryLabel(e)}</p>
                              <div className="action-item-activity-meta">
                                <span className="action-item-activity-icon-wrap">
                                  <FontAwesomeIcon icon={faUser} className="action-item-activity-icon-inline" />
                                </span>
                                <span className="action-item-activity-actor" title={e.employee_id}>
                                  {getEmployeeDisplayName(e.employee_id)}
                                </span>
                                <span className="action-item-activity-time">
                                  {formatDateSafely(e.timestamp) ?? e.timestamp}
                                </span>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                ) : (
                  <p className="action-item-empty-state">No activity yet. Changes to status and details will appear here.</p>
                )}
              </div>
            )}
          </div>

          {/* Comments Section */}
          <div className="action-item-section">
            <button
              className="action-item-collapsible-header"
              onClick={() => setShowComments(!showComments)}
            >
              <h3 className="action-item-section-title">Comments</h3>
              <div className="action-item-count-badge">
                {commentsList.length}
              </div>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`action-item-chevron ${showComments ? 'open' : ''}`}
              />
            </button>
            {showComments && (
              <div className="action-item-collapsible-content">
                {commentsList.length === 0 && (
                  <p className="action-item-empty-state">No comments yet.</p>
                )}
                <ul className="action-item-comment-list">
                  {commentsList.map((comment, idx) => (
                    <li
                      key={idx}
                      className={`action-item-comment-item ${expandedCommentIndex === idx ? 'is-expanded' : ''}`}
                    >
                      <div className="action-item-comment-avatar">
                        {getEmployeeDisplayName(comment.employee_id)
                          .split(' ')
                          .filter(Boolean)
                          .map((part) => part[0]?.toUpperCase() || '')
                          .join('')
                          .slice(0, 2) || '?'}
                      </div>
                      <div
                        className="action-item-comment-body"
                        role="button"
                        tabIndex={0}
                        onClick={() => setExpandedCommentIndex(expandedCommentIndex === idx ? null : idx)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setExpandedCommentIndex(expandedCommentIndex === idx ? null : idx);
                          }
                        }}
                        aria-expanded={expandedCommentIndex === idx}
                        aria-label={expandedCommentIndex === idx ? 'Collapse comment' : 'Expand comment'}
                      >
                        <p className="action-item-comment-text">{comment.text}</p>
                        <div className="action-item-comment-meta">
                          <span className="action-item-comment-author">
                            {getEmployeeDisplayName(comment.employee_id)}
                          </span>
                          <span className="action-item-comment-time">
                            {formatDateSafely(comment.timestamp) ?? comment.timestamp}
                          </span>
                        </div>
                        {comment.text.length > 120 && (
                          <span className="action-item-comment-expand-hint">
                            {expandedCommentIndex === idx ? 'Show less' : 'Read more'}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="action-item-comment-add">
                  <textarea
                    value={newCommentText}
                    onChange={(e) => setNewCommentText(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    className="action-item-comment-input"
                    maxLength={COMMENT_MAX_LENGTH}
                    disabled={isAddingComment}
                  />
                  <p className="action-item-comment-char-count" aria-live="polite">
                    {newCommentText.length} / {COMMENT_MAX_LENGTH} characters
                  </p>
                  <button
                    type="button"
                    className="action-item-comment-submit"
                    onClick={handleAddComment}
                    disabled={!newCommentText.trim() || isAddingComment}
                  >
                    {isAddingComment ? 'Adding...' : 'Add Comment'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Documents Section */}
          <div className="action-item-section">
            <button
              className="action-item-collapsible-header"
              onClick={() => setShowDocuments(!showDocuments)}
            >
              <FontAwesomeIcon icon={faPaperclip} className="action-item-section-icon" />
              <h3 className="action-item-section-title">Documents</h3>
              <div className="action-item-count-badge">
                {documents.length}
              </div>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`action-item-chevron ${showDocuments ? 'open' : ''}`}
              />
            </button>
            {showDocuments && (
              <div className="action-item-collapsible-content">
                <div className="action-item-docs-actions">
                  <label className="action-item-docs-upload-btn">
                    <FontAwesomeIcon icon={faUpload} />
                    {isUploading ? 'Uploading...' : 'Upload File'}
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      disabled={isUploading}
                    />
                  </label>
                  <button
                    type="button"
                    className="action-item-docs-browse-btn"
                    onClick={() => setShowFileBrowser(true)}
                  >
                    <FontAwesomeIcon icon={faLink} />
                    Link Existing
                  </button>
                </div>

                {isLoadingDocs ? (
                  <div className="action-item-docs-loading">
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading documents...
                  </div>
                ) : documents.length === 0 ? (
                  <p className="action-item-empty-state">No documents attached yet.</p>
                ) : (
                  <ul className="action-item-docs-list">
                    {documents.map((doc) => (
                      <li key={doc.id} className="action-item-docs-item">
                        <FontAwesomeIcon icon={faFile} className="action-item-docs-file-icon" />
                        <div className="action-item-docs-file-info">
                          <span className="action-item-docs-file-name">{doc.name}</span>
                          <span className="action-item-docs-file-size">{formatFileSize(doc.size)}</span>
                        </div>
                        <div className="action-item-docs-file-actions">
                          <button
                            type="button"
                            className="action-item-docs-action-btn"
                            onClick={() => {
                              const url = doc.webUrl;
                              if (!url) return;
                              const separator = url.includes('?') ? '&' : '?';
                              window.open(`${url}${separator}web=1`, '_blank');
                            }}
                            aria-label={`Open ${doc.name} in new tab`}
                            title="Open in new tab"
                          >
                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                          </button>
                          <button
                            type="button"
                            className="action-item-docs-action-btn"
                            onClick={() => {
                              const downloadUrl = doc['@microsoft.graph.downloadUrl'];
                              if (!downloadUrl) return;
                              const a = document.createElement('a');
                              a.href = downloadUrl;
                              a.download = doc.name;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                            }}
                            aria-label={`Download ${doc.name}`}
                            title="Download"
                          >
                            <FontAwesomeIcon icon={faDownload} />
                          </button>
                          <button
                            type="button"
                            className="action-item-docs-action-btn action-item-docs-remove-btn"
                            onClick={() => setRemoveConfirmId(doc.id)}
                            aria-label={`Remove ${doc.name}`}
                            title="Remove"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="action-item-error-message" style={{ color: '#dc3545', padding: '10px', marginBottom: '10px' }}>
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-item-actions">
            {isEditMode ? (
              <>
                <button 
                  className="action-item-edit-button" 
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <FontAwesomeIcon icon={faSave} />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button 
                  className="action-item-cancel-button" 
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  <FontAwesomeIcon icon={faBan} />
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button className="action-item-edit-button" onClick={handleEditClick}>
                  <FontAwesomeIcon icon={faEdit} />
                  Edit Details
                </button>
                <button 
                  className="action-item-delete-button" 
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  {isDeleting ? 'Deleting...' : 'Delete Item'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        message={`Are you sure you want to delete "${task?.task_title || 'this task'}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* Remove Document Confirmation Modal */}
      <ConfirmationModal
        isOpen={removeConfirmId !== null}
        onClose={() => setRemoveConfirmId(null)}
        onConfirm={handleRemoveDocument}
        message="Are you sure you want to remove this document from the action item?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />

      {/* SharePoint File Browser Modal */}
      <SharePointFileBrowser
        isOpen={showFileBrowser}
        onClose={() => setShowFileBrowser(false)}
        onFilesSelected={handleFilesLinked}
        isLinking={isLinkingFiles}
      />
    </div>
  );
};

export default ActionItemDetailsPanel;

